import { useContext, forwardRef, useState, useRef, useEffect, type PropsWithoutRef } from 'react';
import styled from 'styled-components';

import { Button, ExpandCollapse, Flex, useI18n } from '@pega/cosmos-react-core';
import type {
  NoChildrenProp,
  OmitStrict,
  RefElement,
  WithAttributes
} from '@pega/cosmos-react-core';
import { StyledEditorContainer } from '@pega/cosmos-react-rte/lib/components/RichTextEditor/RichTextEditor.styles';
import { RichTextViewer } from '@pega/cosmos-react-rte';
import type { RichTextViewerProps } from '@pega/cosmos-react-rte';

import { FeedContext } from './Feed.context';
import type { FeedContentProps } from './Feed.types';

type FeedRichTextViewerProps = WithAttributes<
  'div',
  OmitStrict<FeedContentProps, 'children'> &
    NoChildrenProp & {
      content: string;
      onLinkRender?: RichTextViewerProps['onMarkdownLinkRender'];
    }
>;

const StyledContent = styled.div`
  ${StyledEditorContainer} {
    padding: 0;
  }
`;

const FeedRichTextViewer = forwardRef<
  RefElement<FeedRichTextViewerProps>,
  PropsWithoutRef<FeedRichTextViewerProps>
>(function FeedRichTextViewer(
  { content = '', maxContentHeight = Infinity, onLinkRender, ...restProps },
  ref
) {
  const t = useI18n();

  const { interactionRenderers, markdownMap } = useContext(FeedContext);
  const rtvRef = useRef<HTMLDivElement>(null);

  const [collapsed, setCollapsed] = useState(true);
  const [exceedsMaxHeight, setExceedsMaxHeight] = useState(true);

  useEffect(() => {
    if (!rtvRef.current) return;

    const observer = new ResizeObserver(entries => {
      const exceedsHeight = entries.some(({ target, contentRect }) => {
        if (target !== rtvRef.current) return;

        return contentRect.height > maxContentHeight;
      });

      setExceedsMaxHeight(exceedsHeight);
    });

    observer.observe(rtvRef.current);

    return () => {
      observer.disconnect();
    };
  }, [rtvRef.current, maxContentHeight]);

  return (
    <Flex as={StyledContent} ref={ref} container={{ direction: 'column', gap: 1 }}>
      <ExpandCollapse
        collapsed={collapsed}
        min={exceedsMaxHeight ? `${maxContentHeight}px` : 'max-content'}
        {...restProps}
      >
        <RichTextViewer
          ref={rtvRef}
          content={content}
          type='markdown'
          interactionRenderers={interactionRenderers}
          markdownMap={markdownMap}
          onMarkdownLinkRender={onLinkRender}
        />
      </ExpandCollapse>
      {exceedsMaxHeight && (
        <Flex container={{ justify: 'center' }}>
          <Button variant='link' onClick={() => setCollapsed(current => !current)}>
            {collapsed ? t('show_more') : t('show_less')}
          </Button>
        </Flex>
      )}
    </Flex>
  );
});

export default FeedRichTextViewer;
